/**
 * O idioma do backoffice.
 *
 * ===========================================================================
 * POR QUE UM MÓDULO PRÓPRIO, E NÃO `@planuze/translate`
 * ===========================================================================
 * O catálogo compartilhado é importado ESTATICAMENTE por `packages/translate/src/catalogs.ts`
 * (`import ptBR from '../locales/pt-BR.json'`), e os dois arquivos já somam ~595 KB. Toda chave
 * acrescentada ali entra no bundle de TODO consumidor — inclusive o do Electron, que não tem
 * nada a ver com a tela de operação do staff.
 *
 * O site já resolveu o mesmo problema do mesmo jeito (`apps/web/src/content/`), e a razão vale
 * igual aqui: conteúdo de UMA superfície mora com ela.
 *
 * ===========================================================================
 * DUAS DECISÕES QUE VALEM SER DITAS
 * ===========================================================================
 * O locale é PERSISTIDO em `localStorage`, não derivado do navegador a cada carga: quem opera o
 * backoffice escolhe uma vez e a escolha vale nas próximas sessões. A primeira visita, aí sim,
 * pergunta ao navegador — abrir em inglês para quem tem o sistema em inglês é melhor do que
 * abrir em português e obrigar a trocar.
 *
 * `pt-BR` é o padrão de queda porque é o idioma em que o backoffice foi escrito; um idioma sem
 * tradução completa cairia em texto faltando, e falta de texto numa tela de operação é pior que
 * texto no idioma errado.
 */

export const ADMIN_LOCALES = ['pt-BR', 'en-US'] as const;

export type AdminLocale = (typeof ADMIN_LOCALES)[number];

export const DEFAULT_ADMIN_LOCALE: AdminLocale = 'pt-BR';

/** Chave do `localStorage`. Namespaced para não colidir com o resto do domínio. */
export const ADMIN_LOCALE_STORAGE_KEY = 'planuze.admin.locale';

export function isAdminLocale(value: unknown): value is AdminLocale {
  return typeof value === 'string' && (ADMIN_LOCALES as readonly string[]).includes(value);
}

/**
 * O idioma que o navegador pede, quando ele é um dos que publicamos.
 *
 * Casa por PREFIXO de idioma (`en` casa `en-US`), porque `navigator.language` traz a variante
 * regional do usuário (`en-GB`, `pt-PT`) e recusá-la por não ser exatamente a nossa devolveria
 * português a quem pediu inglês.
 */
export function localeFromNavigator(idiomas: readonly string[]): AdminLocale | null {
  for (const pedido of idiomas) {
    const prefixo = pedido.toLowerCase().split('-')[0];
    const casa = ADMIN_LOCALES.find((nosso) => nosso.toLowerCase().split('-')[0] === prefixo);
    if (casa !== undefined) return casa;
  }
  return null;
}

/**
 * O locale inicial: o que ficou guardado, senão o do navegador, senão o padrão.
 *
 * A leitura é tolerante de propósito — `localStorage` lança em contexto sem permissão (modo
 * restrito, iframe de terceiro), e o backoffice não pode deixar de abrir por causa da
 * preferência de idioma.
 */
export function resolveInitialLocale(
  ler: () => string | null,
  idiomasDoNavegador: readonly string[],
): AdminLocale {
  let guardado: string | null = null;
  try {
    guardado = ler();
  } catch {
    guardado = null;
  }
  if (isAdminLocale(guardado)) return guardado;
  return localeFromNavigator(idiomasDoNavegador) ?? DEFAULT_ADMIN_LOCALE;
}
